import { useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Edit3, Calendar, Flag, UserCircle, FolderKanban, Repeat, Hash, MoreHorizontal, Copy, Trash2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import TaskStatusBadge from './TaskStatusBadge';
import { TASK_STATUSES, getStatusConfig, getDateCategory } from '../lib/taskStatus';
import { format, parseISO, isToday, isTomorrow, isPast } from 'date-fns';

const formatTaskDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'MMM d');
  } catch { return dateStr; }
};

const getDateColor = (dateStr, completed) => {
  if (!dateStr || completed) return 'text-gray-400';
  try {
    const date = parseISO(dateStr);
    if (isPast(date) && !isToday(date)) return 'text-red-500';
    if (isToday(date)) return 'text-orange-500';
    return 'text-blue-500';
  } catch { return 'text-gray-500'; }
};

const KanbanCard = ({ task, index, onEdit, onDelete, onDuplicate, onStatusChange, isManager }) => {
  const isCompleted = task.status === 'completed';
  return (
    <Draggable draggableId={task.task_id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`mb-2 ${snapshot.isDragging ? 'z-50' : ''}`}
        >
          <Card className={`p-3 hover:shadow-md transition-shadow dark:bg-slate-800 dark:border-slate-700 ${isCompleted ? 'opacity-60' : ''} ${snapshot.isDragging ? 'shadow-xl ring-2 ring-[#4A9CC7]/40 rotate-1' : ''}`} data-testid={`kanban-card-${task.task_id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <p className={`text-sm font-medium flex-1 ${isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                {task.title}
              </p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground shrink-0">
                    <MoreHorizontal className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="dark:bg-slate-800 dark:border-slate-700">
                  <DropdownMenuItem onClick={() => onEdit(task)}><Edit3 className="w-3 h-3 mr-2" />Edit</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onDuplicate(task.task_id)}><Copy className="w-3 h-3 mr-2" />Duplicate</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onDelete(task.task_id)} className="text-red-600"><Trash2 className="w-3 h-3 mr-2" />Delete</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              <TaskStatusBadge
                status={task.status || 'assigned'}
                requiresApproval={task.requires_approval}
                isManager={isManager}
                onStatusChange={(newStatus) => onStatusChange(task.task_id, newStatus)}
                compact
              />
              {task.priority && (
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 ${
                  task.priority === 'high' || task.priority === 'urgent' ? 'text-red-600 border-red-200' :
                  task.priority === 'medium' ? 'text-amber-600 border-amber-200' : 'text-emerald-600 border-emerald-200'
                }`}>
                  <Flag className="w-2.5 h-2.5 mr-0.5" />
                  {task.priority}
                </Badge>
              )}
              {task.requires_approval && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 text-amber-600 border-amber-200">
                  Approval
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]">
              {task.due_date && (
                <span className={`flex items-center gap-0.5 ${getDateColor(task.due_date, isCompleted)}`}>
                  <Calendar className="w-2.5 h-2.5" />
                  {formatTaskDate(task.due_date)}
                </span>
              )}
              {task.assigned_to_name && (
                <span className="flex items-center gap-0.5 text-purple-600 dark:text-purple-400">
                  <UserCircle className="w-2.5 h-2.5" />
                  {task.assigned_to_name}
                </span>
              )}
              {task.workspace_name && (
                <span className="flex items-center gap-0.5 text-muted-foreground">
                  <FolderKanban className="w-2.5 h-2.5" />
                  {task.workspace_name}
                </span>
              )}
              {task.is_recurring && (
                <span className="flex items-center gap-0.5 text-[#4A9CC7]">
                  <Repeat className="w-2.5 h-2.5" />
                </span>
              )}
            </div>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

const KanbanBoard = ({ tasks, groupBy, onEdit, onDelete, onDuplicate, onStatusChange, isManager, allMembers }) => {
  const columns = useMemo(() => {
    if (groupBy === 'status') {
      return TASK_STATUSES.map(s => ({
        id: s.value,
        title: s.label,
        dotColor: s.dotColor,
        tasks: tasks.filter(t => (t.status || 'assigned') === s.value),
      }));
    }
    if (groupBy === 'assignee') {
      const assigneeMap = {};
      const unassigned = [];
      tasks.forEach(t => {
        if (t.assigned_to && t.assigned_to_name) {
          if (!assigneeMap[t.assigned_to]) {
            assigneeMap[t.assigned_to] = { name: t.assigned_to_name, tasks: [] };
          }
          assigneeMap[t.assigned_to].tasks.push(t);
        } else {
          unassigned.push(t);
        }
      });
      const cols = Object.entries(assigneeMap).map(([id, data]) => ({
        id, title: data.name, dotColor: 'bg-purple-400', tasks: data.tasks,
      }));
      cols.unshift({ id: 'unassigned', title: 'Unassigned', dotColor: 'bg-gray-400', tasks: unassigned });
      return cols;
    }
    if (groupBy === 'date') {
      const overdue = [], today = [], upcoming = [], noDate = [];
      tasks.forEach(t => {
        const cat = getDateCategory(t.due_date, t.status === 'completed');
        if (cat === 'overdue') overdue.push(t);
        else if (cat === 'today') today.push(t);
        else if (cat === 'upcoming') upcoming.push(t);
        else noDate.push(t);
      });
      return [
        { id: 'overdue', title: 'Overdue', dotColor: 'bg-red-500', tasks: overdue },
        { id: 'today', title: 'Today', dotColor: 'bg-orange-500', tasks: today },
        { id: 'upcoming', title: 'Upcoming', dotColor: 'bg-blue-500', tasks: upcoming },
        { id: 'no_date', title: 'No Due Date', dotColor: 'bg-gray-400', tasks: noDate },
      ];
    }
    return [];
  }, [tasks, groupBy]);

  const handleDragEnd = (result) => {
    const { draggableId, source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    // Only support drag-to-change-status for status grouping
    if (groupBy === 'status') {
      const newStatus = destination.droppableId;
      onStatusChange(draggableId, newStatus);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4 min-h-[400px]" data-testid="kanban-board">
        {columns.map(col => (
          <div key={col.id} className="flex-shrink-0 w-[280px]" data-testid={`kanban-col-${col.id}`}>
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className={`w-2.5 h-2.5 rounded-full ${col.dotColor}`} />
              <h3 className="text-sm font-semibold text-foreground">{col.title}</h3>
              <Badge variant="secondary" className="text-[10px] h-5 px-1.5">{col.tasks.length}</Badge>
            </div>
            <Droppable droppableId={col.id} isDropDisabled={groupBy !== 'status'}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`rounded-lg p-2 min-h-[200px] transition-colors ${
                    snapshot.isDraggingOver
                      ? 'bg-[#4A9CC7]/10 ring-2 ring-[#4A9CC7]/30 ring-dashed'
                      : 'bg-muted/30 dark:bg-slate-900/50'
                  }`}
                >
                  {col.tasks.length === 0 && !snapshot.isDraggingOver ? (
                    <div className="text-xs text-muted-foreground text-center py-8">No tasks</div>
                  ) : (
                    col.tasks.map((task, index) => (
                      <KanbanCard
                        key={task.task_id}
                        task={task}
                        index={index}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onDuplicate={onDuplicate}
                        onStatusChange={onStatusChange}
                        isManager={isManager}
                      />
                    ))
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        ))}
      </div>
    </DragDropContext>
  );
};

export default KanbanBoard;
